import { useMemo, useCallback, useState, useEffect } from 'react';
import { Modal, Button, message } from 'antd';
import dayjs from 'dayjs';
import copy from 'copy-to-clipboard';
import config from '@config';
import { invitationNameTitles } from '@utils/constants';
import './InvitationScriptModal.less';

const InvitationScriptModal = ({ open, onCancel, guestData, invitationOne }) => {
    // 可编辑的邀请话术状态
    const [editableDetailedScript, setEditableDetailedScript] = useState('');
    const [editableSummarizedScript, setEditableSummarizedScript] = useState('');

    // 生成称谓
    const getNameTitle = useCallback(() => {
        let nameTitle = '';
        if (invitationOne?.nameTitle) {
            switch (invitationOne.nameTitle) {
                case 1:
                    break;
                case 2:
                    nameTitle = guestData?.memGender === 0 ? '女士' : guestData?.memGender === 1 ? '先生' : '先生/女士';
                    break;
                case 3:
                case 4:
                case 5:
                    nameTitle = invitationNameTitles.find(t => t.type === invitationOne.nameTitle)?.title || '';
                    break;
                case 6:
                    if (guestData?.ssoMemberWorkExperiencesEntities && guestData.ssoMemberWorkExperiencesEntities.length) {
                        nameTitle = guestData.ssoMemberWorkExperiencesEntities[0]?.unitPost || '';
                    }
                    break;
                default:
                    break;
            }
        }
        return nameTitle;
    }, [guestData, invitationOne]);

    // 生成邀请链接
    const getInvitationLink = useCallback(() => {
        return `${config.INVITATION_PREFIX}/${guestData?.invitationUsers?.userCode}`;
    }, [guestData]);

    // 处理邀请话术中的链接占位符
    const processInvitationText = useCallback(
        text => {
            const invitationLink = getInvitationLink();
            if (text.indexOf('$邀请链接$') > -1) {
                return text.replace(/[ 　]*\$邀请链接\$[ 　]*/g, ` ${invitationLink} `);
            }
            return text;
        },
        [getInvitationLink],
    );

    // 判断上午/下午/晚上
    const getAmOrPm = timeStr => {
        const hour = dayjs(timeStr).hour();
        if (hour < 12) {
            return '上午';
        } else if (hour < 18) {
            return '下午';
        } else {
            return '晚上';
        }
    };

    // 生成联系人信息
    const getContactInfo = useCallback(() => {
        if (!invitationOne?.contactInfos) return '';

        const contacts = JSON.parse(invitationOne.contactInfos) || [];
        if (!Array.isArray(contacts) || !contacts.length) return '';

        let contactText = '　　联系人：';
        contacts.forEach((contact, index) => {
            contactText += `${contact.userName} ${contact.phone}`;
            if (index < contacts.length - 1) {
                contactText += '、';
            }
        });
        return contactText;
    }, [invitationOne]);

    // 整合任务：按会议场次和时段分组
    const groupTasksByHallAndPeriod = useCallback(tasks => {
        if (!tasks || tasks.length === 0) return [];

        // 先按会议场次分组
        const hallGroups = {};
        tasks.forEach(task => {
            const hallName = task.conferenceHallName;
            if (!hallGroups[hallName]) {
                hallGroups[hallName] = [];
            }
            hallGroups[hallName].push(task);
        });

        // 对每个场次按时段（上午/下午/晚上）分组
        const result = [];
        Object.entries(hallGroups).forEach(([hallName, hallTasks]) => {
            // 按开始时间排序
            hallTasks.sort((a, b) => dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf());

            // 按时段分组
            const periodGroups = {};
            hallTasks.forEach(task => {
                const date = dayjs(task.startTime).format('YYYY-MM-DD');
                const period = getAmOrPm(task.startTime);
                const key = `${date}_${period}`;

                if (!periodGroups[key]) {
                    periodGroups[key] = [];
                }
                periodGroups[key].push(task);
            });

            // 整合每个时段的任务
            Object.values(periodGroups).forEach(periodTasks => {
                if (periodTasks.length === 0) return;

                // 按时间排序
                periodTasks.sort((a, b) => dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf());

                const startTime = periodTasks[0].startTime;
                const endTime = periodTasks[periodTasks.length - 1].endTime;

                // 收集任务类型和内容
                const taskTypes = [];
                const contents = {};

                periodTasks.forEach(task => {
                    if (!taskTypes.includes(task.taskName)) {
                        taskTypes.push(task.taskName);
                    }

                    // 收集需要展示内容的任务（大会主席和主持嘉宾不展示内容）
                    if (task.taskName !== '大会主席' && task.taskName !== '主持嘉宾') {
                        if (!contents[task.taskName]) {
                            contents[task.taskName] = [];
                        }
                        contents[task.taskName].push({
                            title: task.title,
                            taskName: task.taskName,
                        });
                    }
                });

                result.push({
                    conferenceHallName: hallName,
                    startTime,
                    endTime,
                    taskTypes,
                    contents,
                    tasks: periodTasks,
                });
            });
        });

        // 按开始时间排序
        result.sort((a, b) => dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf());

        return result;
    }, []);

    // 生成详细版邀请话术
    const detailedScript = useMemo(() => {
        if (!guestData || !invitationOne) return '';

        const nameTitle = getNameTitle();
        const scriptText = processInvitationText(guestData.smsInfo || '');

        let result = `尊敬的${guestData.memUsername}${nameTitle}：\n${scriptText}\n`;

        // 嘉宾任务（过滤掉已拒绝的任务、陪同人员 taskId 24）
        const validTasks = guestData?.agendasGuestsList && Array.isArray(guestData.agendasGuestsList) ? guestData.agendasGuestsList.filter(task => task.invitationAccepted !== 2 && Number(task.taskId) !== 24) : [];

        if (validTasks.length) {
            result += '　　诚挚邀请您出席本次大会并出席以下环节：\n';

            validTasks.forEach((task, index) => {
                const prefix = validTasks.length > 1 ? `　　${index + 1}. ` : '　　';

                result += prefix;
                result += `${dayjs(task.startTime).format('M月D日')}${getAmOrPm(task.startTime)} `;
                result += `${dayjs(task.startTime).format('HH:mm')}～${dayjs(task.endTime).format('HH:mm')} `;
                result += `${task?.conferenceHallName}，`;
                result += `担任${task.taskName}`;

                if (task.taskName === '大会主席' || task.taskName === '主持嘉宾') {
                    result += '。\n';
                } else {
                    result += `，${task.title}`;
                    if (task.taskName === '报告嘉宾') {
                        result += '（您可根据内容优化）';
                    }
                    result += '。\n';
                }
            });
        }

        result += '　　期盼您的回复！\n';

        const contactInfo = getContactInfo();
        if (contactInfo) {
            result += contactInfo;
        }

        return result;
    }, [guestData, invitationOne, getNameTitle, processInvitationText, getContactInfo]);

    // 生成概括版邀请话术（整合任务）
    const summarizedScript = useMemo(() => {
        if (!guestData || !invitationOne) return '';

        const nameTitle = getNameTitle();
        const scriptText = processInvitationText(guestData.smsInfo || '');

        let result = `尊敬的${guestData.memUsername}${nameTitle}：\n${scriptText}\n`;

        // 整合任务（过滤掉已拒绝的任务、陪同人员 taskId 24）
        const validTasks = guestData?.agendasGuestsList && Array.isArray(guestData.agendasGuestsList) ? guestData.agendasGuestsList.filter(task => task.invitationAccepted !== 2 && Number(task.taskId) !== 24) : [];

        if (validTasks.length) {
            result += '　　诚挚邀请您出席本次大会并出席以下环节：\n';

            const groupedTasks = groupTasksByHallAndPeriod(validTasks);

            groupedTasks.forEach((group, index) => {
                const prefix = groupedTasks.length > 1 ? `　　${index + 1}. ` : '　　';

                result += prefix;
                result += `${dayjs(group.startTime).format('M月D日')} `;
                result += `${dayjs(group.startTime).format('HH:mm')}～${dayjs(group.endTime).format('HH:mm')} `;
                result += `${group.conferenceHallName}，`;

                // 处理任务类型
                const taskTypes = group.taskTypes;
                if (taskTypes.length === 1) {
                    result += `担任${taskTypes[0]}`;
                } else if (taskTypes.length === 2) {
                    result += `担任${taskTypes[0]}和${taskTypes[1]}`;
                } else {
                    const lastType = taskTypes[taskTypes.length - 1];
                    const otherTypes = taskTypes.slice(0, -1).join('、');
                    result += `担任${otherTypes}和${lastType}`;
                }
                result += '。';

                // 添加具体内容
                const contentParts = [];
                Object.entries(group.contents).forEach(([type, items]) => {
                    if (items.length > 0) {
                        items.forEach(item => {
                            contentParts.push({
                                title: item.title,
                                type: type,
                            });
                        });
                    }
                });

                if (contentParts.length > 0) {
                    const formattedParts = contentParts.map((item, idx) => {
                        const prefix = contentParts.length > 1 ? `【环节${idx + 1}】` : '';
                        let itemText = `${prefix}${item.title}`;
                        if (item.type === '报告嘉宾') {
                            itemText += '（您可根据内容优化）';
                        }
                        return itemText;
                    });
                    result += formattedParts.join('；');
                    result += '。';
                }
                result += '\n';
            });
        }

        result += '　　期盼您的回复！\n';

        const contactInfo = getContactInfo();
        if (contactInfo) {
            result += contactInfo;
        }

        return result;
    }, [guestData, invitationOne, getNameTitle, processInvitationText, getContactInfo, groupTasksByHallAndPeriod]);

    // 当生成的话术变化时，更新可编辑状态
    useEffect(() => {
        setEditableDetailedScript(detailedScript);
    }, [detailedScript]);

    useEffect(() => {
        setEditableSummarizedScript(summarizedScript);
    }, [summarizedScript]);

    // 复制详细版
    const handleCopyDetailed = () => {
        copy(editableDetailedScript);
        message.success('复制详细版邀请话术成功');
    };

    // 复制概括版
    const handleCopySummarized = () => {
        copy(editableSummarizedScript);
        message.success('复制概括版邀请话术成功');
    };

    // 判断是否有多个任务
    const hasMultipleTasks = guestData?.agendasGuestsList && Array.isArray(guestData.agendasGuestsList) && guestData.agendasGuestsList.length > 1;

    // 判断是否需要整合任务（有同一会场或同一时段的多个任务）
    const needsSummarization = useMemo(() => {
        if (!hasMultipleTasks) return false;

        const tasks = guestData.agendasGuestsList;

        // 按会议场次分组
        const hallGroups = {};
        tasks.forEach(task => {
            const hallName = task.conferenceHallName;
            if (!hallGroups[hallName]) {
                hallGroups[hallName] = [];
            }
            hallGroups[hallName].push(task);
        });

        // 检查是否有任何会场有多个任务，或者有多个任务可以按时段整合
        for (const hallTasks of Object.values(hallGroups)) {
            if (hallTasks.length > 1) {
                // 检查这些任务是否在同一时段
                const periodGroups = {};
                hallTasks.forEach(task => {
                    const date = dayjs(task.startTime).format('YYYY-MM-DD');
                    const period = getAmOrPm(task.startTime);
                    const key = `${date}_${period}`;

                    if (!periodGroups[key]) {
                        periodGroups[key] = [];
                    }
                    periodGroups[key].push(task);
                });

                // 如果有任何时段有多个任务，说明需要整合
                for (const periodTasks of Object.values(periodGroups)) {
                    if (periodTasks.length > 1) {
                        return true;
                    }
                }
            }
        }

        return false;
    }, [guestData, hasMultipleTasks]);

    return (
        <Modal title="复制邀请话术" open={open} onCancel={onCancel} centered width={needsSummarization ? 1000 : 600} footer={null} className="invitation-script-modal">
            <div className={`script-container ${!needsSummarization ? 'single-column' : ''}`}>
                <div className="script-column">
                    {needsSummarization && (
                        <>
                            <div className="script-header">
                                <h3>详细版出席环节邀请话术：</h3>
                            </div>
                            <div className="script-description">罗列每个日程任务，具体、详尽。</div>
                        </>
                    )}
                    <div className="script-content">
                        <textarea value={editableDetailedScript} onChange={e => setEditableDetailedScript(e.target.value)} rows={15} />
                    </div>
                    <div className="script-footer">
                        <Button type="primary" onClick={handleCopyDetailed} block>
                            复制邀请话术
                        </Button>
                    </div>
                </div>

                {needsSummarization && (
                    <div className="script-column">
                        <div className="script-header">
                            <h3>整合版出席环节邀请话术：</h3>
                        </div>
                        <div className="script-description">整合日程任务，合并同一会议场次日程任务。</div>
                        <div className="script-content">
                            <textarea value={editableSummarizedScript} onChange={e => setEditableSummarizedScript(e.target.value)} rows={15} />
                        </div>
                        <div className="script-footer">
                            <Button onClick={handleCopySummarized} block>
                                复制邀请话术
                            </Button>
                        </div>
                    </div>
                )}
            </div>
            <div className="script-notice">可在上述文本框中临时编辑话术，修改内容不会保存到系统。</div>
        </Modal>
    );
};

export default InvitationScriptModal;
